import { useCallback } from 'react'
import { RequiredDocument } from 'types'
import { RequestError, applySearchParametersToUrl, fetchWithTimeout, logFactory } from 'utils'
import { useHttpClientConfig } from '../useHttpClientConfig'
import { useServiceCountryUrl } from '../useServiceCountryUrl'

const log = logFactory('useDocumentsService')

const useDocumentsService = () => {
  const { url: serviceUrl } = useServiceCountryUrl('cf-document')
  const { getConfig } = useHttpClientConfig()

  const getServiceUrl = useCallback(() => serviceUrl.href, [serviceUrl])

  const makeRequest = useCallback(
    async <TResponse>(pathname: string, searchParams: Record<string, unknown>) => {
      const baseUrl = getServiceUrl()
      const url = new URL(pathname, baseUrl)
      const hydratedUrl = applySearchParametersToUrl(url, searchParams)
      const configInit = await getConfig()
      const response = await fetchWithTimeout(hydratedUrl.href, configInit)

      if (!response.ok) {
        throw new RequestError(`OPS request failed: ${response.status} ${response.statusText}`, response.status)
      }
      const json = await response.json()

      return (json?.data ?? json) as TResponse
    },
    [getServiceUrl, getConfig]
  )

  const getRequiredDocuments = useCallback(
    (args: { country: string; customerGid: string; opportunityGid?: string }) => {
      const search = {
        country: args.country,
        customerGid: args.customerGid,
        opportunityGid: args.opportunityGid,
        pageSize: 50,
      }

      log('Fetching required-documents', search)

      return makeRequest<RequiredDocument[]>('/operations/v1/required-documents', search)
    },
    [makeRequest]
  )

  return {
    getRequiredDocuments,
  }
}

export { useDocumentsService }
